/**
 * Design Tokens - Single Source of Truth
 * 
 * All spacing, typography, dimensions, and colors MUST use these constants.
 * DO NOT use arbitrary Tailwind classes like text-xl, px-4, text-gray-600, etc.
 * 
 * Usage:
 * import { TYPOGRAPHY, SPACING, DIMENSIONS, COLORS } from '@/lib/design-tokens';
 * className={`${TYPOGRAPHY.PAGE_TITLE} ${SPACING.PAGE_HORIZONTAL} ${COLORS.TEXT_PRIMARY}`}
 */

// ============= COLORS =============
// Core 3-color palette with semantic states
export const COLORS = {
  // Core 3-color palette
  PRIMARY: 'bg-primary text-primary-foreground',
  SECONDARY: 'bg-secondary text-secondary-foreground',
  ACCENT: 'text-accent',
  
  // Text hierarchy
  TEXT_PRIMARY: 'text-foreground',
  TEXT_SECONDARY: 'text-muted-foreground',
  TEXT_MUTED: 'text-muted-foreground/60',
  
  // Backgrounds
  BG_CARD: 'bg-card',
  BG_SECONDARY: 'bg-secondary',
  BG_MUTED: 'bg-muted',
  
  // Borders
  BORDER: 'border-border',
  
  // Semantic states
  SUCCESS: 'text-success',
  WARNING: 'text-warning',
  DESTRUCTIVE: 'text-destructive',
  
  // Personality trait colors (PRESERVED - do not use for UI)
  TRAIT_DOMINANCE: '#ef4444',     // Red - A factor
  TRAIT_EXTRAVERSION: '#f59e0b',  // Amber - B factor
  TRAIT_PATIENCE: '#3b82f6',      // Blue - C factor
  TRAIT_FORMALITY: '#10b981',     // Green - D factor
  TRAIT_OBJECTIVITY: '#8b5cf6',   // Purple - E factor
} as const;

// ============= CHART DESIGN SYSTEM =============
// Behavioral chart specifications (InlineBehavioralChart)
// @see src/lib/chart-constants.ts for complete specifications
export const CHART = {
  // Aspect ratios
  ASPECT_WIDE: '3:2',              // Desktop/horizontal displays
  ASPECT_COMPACT: null,            // Fixed dimensions instead
  
  // Container dimensions
  CONTAINER_WIDE_WIDTH: 'w-1/2',   // 50% width
  CONTAINER_COMPACT_WIDTH: 'w-full',
  CONTAINER_COMPACT_HEIGHT: 'h-24', // 96px
  
  // Content dimensions (before rotation)
  CONTENT_WIDE: 'w-full h-full',
  CONTENT_COMPACT: 'w-12 h-16',
  
  // Dot sizes
  DOT_RADIUS_WIDE: 7.8,            // px for ABCD traits
  DOT_RADIUS_COMPACT: 5.2,         // px for ABCD traits
  DOT_OBJECTIVITY_WIDE: 'w-4 h-4', // 16px
  DOT_OBJECTIVITY_COMPACT: 'w-2.5 h-2.5', // 10px
  
  // Line styles
  LINE_STROKE_WIDTH: 2,            // Connecting lines
  REFERENCE_STROKE_WIDTH: 1,       // 50th percentile line
  REFERENCE_DASH: '3 3',           // Dash pattern
  
  // Margins
  MARGIN_WIDE: 10,                 // px
  MARGIN_COMPACT: 5,               // px
} as const;

// ============= TYPOGRAPHY HIERARCHY =============
// Use semantic names, not sizes
export const TYPOGRAPHY = {
  // Display - Hero text, major headings
  DISPLAY_XL: 'text-display-xl',      // 72px - Landing hero
  DISPLAY_L: 'text-display-l',        // 56px - Section hero
  DISPLAY_M: 'text-display-m',        // 40px - Page hero
  
  // Headings - Section titles
  HEADING_L: 'text-heading-l',        // 32px - Page title
  HEADING_M: 'text-heading-m',        // 24px - Section title
  HEADING_S: 'text-heading-s',        // 20px - Subsection title
  
  // Body - Content text
  BODY_L: 'text-body-l',              // 18px - Lead paragraph
  BODY_M: 'text-body-m',              // 16px - Primary body
  BODY_S: 'text-body-s',              // 14px - Secondary body
  
  // Label - UI labels, tags
  LABEL: 'text-label',                // 12px - Labels, badges
  
  // Semantic aliases for common use cases
  PAGE_TITLE: 'text-heading-l',       // Main page title
  PAGE_DESCRIPTION: 'text-body-m text-muted-foreground',
  SECTION_TITLE: 'text-heading-m',
  SECTION_DESCRIPTION: 'text-body-s text-muted-foreground',
  CARD_TITLE: 'text-heading-s',
  CARD_DESCRIPTION: 'text-body-s text-muted-foreground',
  TABLE_HEADER: 'text-body-s font-semibold',
  TABLE_CELL: 'text-body-s',
  BUTTON_TEXT: 'text-body-m font-medium',
  INPUT_TEXT: 'text-body-m',
  BADGE_TEXT: 'text-label',
  TOOLTIP_TEXT: 'text-body-s',
} as const;

// ============= SPACING SCALE (8pt Grid) =============
export const SPACING = {
  // Padding/Margin Units
  NONE: '0',
  XXS: '2',           // 8px
  XS: '3',            // 12px
  SM: '4',            // 16px
  MD: '6',            // 24px
  LG: '8',            // 32px
  XL: '12',           // 48px
  XXL: '16',          // 64px
  
  // Page-level spacing (PageContainer enforces these)
  PAGE_HORIZONTAL: 'px-6',           // 24px - Fixed for all pages
  PAGE_VERTICAL: 'py-8',             // 32px top + 24px bottom - Fixed
  PAGE_VERTICAL_TOP: 'pt-8',         // 32px
  PAGE_VERTICAL_BOTTOM: 'pb-6',      // 24px
  
  // Section spacing
  SECTION_GAP: 'space-y-8',          // 32px between major sections
  CARD_GAP: 'space-y-6',             // 24px between cards
  ELEMENT_GAP: 'gap-4',              // 16px between elements
  TIGHT_GAP: 'gap-2',                // 8px tight spacing
  
  // Component padding
  CARD_PADDING: 'p-6',               // 24px - Standard card padding
  SECTION_PADDING: 'p-8',            // 32px - Large section padding
  BUTTON_PADDING_X: 'px-6',          // 24px horizontal
  BUTTON_PADDING_Y: 'py-3',          // 12px vertical
  INPUT_PADDING: 'px-4 py-3',        // 16px x 12px
  BADGE_PADDING: 'px-2 py-1',        // 8px x 4px
} as const;

// ============= COMPONENT DIMENSIONS =============
export const DIMENSIONS = {
  // Button heights
  BUTTON_SM: 'h-8',                  // 32px
  BUTTON_MD: 'h-10',                 // 40px - Default
  BUTTON_LG: 'h-12',                 // 48px
  BUTTON_XL: 'h-14',                 // 56px
  
  // Input heights (must match button for form alignment)
  INPUT_SM: 'h-8',                   // 32px
  INPUT_MD: 'h-10',                  // 40px - Default
  INPUT_LG: 'h-12',                  // 48px
  
  // Icon sizes
  ICON_XS: 'h-3 w-3',                // 12px
  ICON_SM: 'h-4 w-4',                // 16px
  ICON_MD: 'h-5 w-5',                // 20px - Default
  ICON_LG: 'h-6 w-6',                // 24px
  ICON_XL: 'h-8 w-8',                // 32px
  
  // Avatar sizes
  AVATAR_XS: 'h-6 w-6',              // 24px
  AVATAR_SM: 'h-8 w-8',              // 32px
  AVATAR_MD: 'h-10 w-10',            // 40px - Default
  AVATAR_LG: 'h-12 w-12',            // 48px
  AVATAR_XL: 'h-16 w-16',            // 64px
  
  // Card min heights
  CARD_MIN_HEIGHT: 'min-h-[200px]',
  
  // Max widths (content containers)
  CONTENT_SM: 'max-w-2xl',           // 672px
  CONTENT_MD: 'max-w-4xl',           // 896px
  CONTENT_LG: 'max-w-6xl',           // 1152px
  CONTENT_XL: 'max-w-7xl',           // 1280px - PageContainer default
  
  // Sidebar
  SIDEBAR_WIDTH: 'w-64',             // 256px
  SIDEBAR_COLLAPSED: 'w-16',         // 64px
} as const;

// ============= BORDER RADIUS =============
export const RADIUS = {
  NONE: 'rounded-none',
  SM: 'rounded-sm',                  // 6px
  MD: 'rounded-md',                  // 8px - Default
  LG: 'rounded-lg',                  // 12px
  XL: 'rounded-xl',                  // 16px
  FULL: 'rounded-full',
} as const;

// ============= SHADOWS =============
export const SHADOWS = {
  NONE: 'shadow-none',
  XS: 'shadow-xs',
  SM: 'shadow-sm',
  MD: 'shadow-md',                   // Default
  LG: 'shadow-lg',
  XL: 'shadow-xl',
} as const;

// ============= TRANSITIONS =============
export const TRANSITIONS = {
  INSTANT: 'duration-instant',       // 100ms
  FAST: 'duration-fast',             // 200ms - Default
  NORMAL: 'duration-normal',         // 300ms
  SMOOTH: 'duration-smooth',         // 400ms
  
  // Common combinations
  ALL_FAST: 'transition-all duration-fast',
  COLORS_FAST: 'transition-colors duration-fast',
  TRANSFORM_FAST: 'transition-transform duration-fast',
} as const;

// ============= COMPONENT PATTERNS =============
// Common component class combinations
export const PATTERNS = {
  // Cards
  CARD_BASE: `${RADIUS.LG} border border-border bg-card`,
  CARD_INTERACTIVE: `${RADIUS.LG} border border-border bg-card hover:shadow-md ${TRANSITIONS.ALL_FAST} cursor-pointer`,
  
  // Buttons
  BUTTON_BASE: `${RADIUS.MD} ${TRANSITIONS.ALL_FAST} font-medium inline-flex items-center justify-center`,
  BUTTON_PRIMARY: `bg-primary text-primary-foreground hover:bg-primary/90`,
  BUTTON_SECONDARY: `bg-secondary text-secondary-foreground hover:bg-secondary/80`,
  BUTTON_GHOST: `hover:bg-accent hover:text-accent-foreground`,
  
  // Inputs
  INPUT_BASE: `${RADIUS.MD} border border-input bg-background px-4 py-3 ${TYPOGRAPHY.INPUT_TEXT} ${TRANSITIONS.COLORS_FAST}`,
  INPUT_FOCUS: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  
  // Badges
  BADGE_BASE: `${RADIUS.SM} ${SPACING.BADGE_PADDING} ${TYPOGRAPHY.BADGE_TEXT} inline-flex items-center`,
  BADGE_PRIMARY: 'bg-primary/10 text-primary border border-primary/20',
  BADGE_SECONDARY: 'bg-secondary text-secondary-foreground',
  
  // Table
  TABLE_HEADER: `${TYPOGRAPHY.TABLE_HEADER} bg-muted/50 border-b`,
  TABLE_CELL: `${TYPOGRAPHY.TABLE_CELL} border-b`,
  TABLE_ROW: 'hover:bg-muted/50 transition-colors',
} as const;

// ============= VALIDATION HELPERS =============
/**
 * Check if a className string uses design tokens
 * @param className - The className string to check
 * @returns true if valid, false if contains arbitrary values
 */
export function validateTokenUsage(className: string): boolean {
  // Disallowed patterns (arbitrary values)
  const disallowedPatterns = [
    /text-\d+xl/,           // text-2xl, text-3xl, etc.
    /text-\[.*\]/,          // text-[14px], etc.
    /px-\d+(?!\/)/,         // px-4, px-8, etc. (except fractions)
    /py-\d+(?!\/)/,         // py-4, py-8, etc.
    /h-\d+(?!\/)/,          // h-10, h-12, etc.
    /w-\d+(?!\/)/,          // w-10, w-12, etc.
    /gap-\d+(?!\/)/,        // gap-4, gap-6, etc.
  ];
  
  return !disallowedPatterns.some(pattern => pattern.test(className));
}

/**
 * Get suggested token for an arbitrary value
 * @param arbitraryClass - The arbitrary class to replace
 * @returns Suggested token or null
 */
export function suggestToken(arbitraryClass: string): string | null {
  const suggestions: Record<string, string> = {
    'text-xs': TYPOGRAPHY.LABEL,
    'text-sm': TYPOGRAPHY.BODY_S,
    'text-base': TYPOGRAPHY.BODY_M,
    'text-lg': TYPOGRAPHY.BODY_L,
    'text-xl': TYPOGRAPHY.HEADING_S,
    'text-2xl': TYPOGRAPHY.HEADING_M,
    'text-3xl': TYPOGRAPHY.HEADING_L,
    'text-4xl': TYPOGRAPHY.DISPLAY_M,
    'px-4': SPACING.PAGE_HORIZONTAL,
    'py-6': SPACING.PAGE_VERTICAL,
    'h-10': DIMENSIONS.BUTTON_MD,
    'h-12': DIMENSIONS.BUTTON_LG,
  };
  
  return suggestions[arbitraryClass] || null;
}

// Export everything as default for convenience
export default {
  TYPOGRAPHY,
  SPACING,
  DIMENSIONS,
  RADIUS,
  SHADOWS,
  TRANSITIONS,
  PATTERNS,
  CHART,
  validateTokenUsage,
  suggestToken,
};
